import FilterList from '@mui/icons-material/FilterList';
import { buttonClasses, paperClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { debounce } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import FilterCategorySearch from '~components/Filters/FilterCategorySearch';
import FilterMenuItem, { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import { StatusTabValue } from '~components/Filters/OrdersStatusFilter';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { DispatchURLParams } from '~constants/dispatch';
import { SelectedFilter } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import useFilterCategoryLabels from '~hooks/useFilterCategoryLabels';
import { useStores } from '~store';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import useOrdersDispatchFilters, {
  ordersDispatchFilterKeyBySelectedFilter,
} from '../../hooks/useOrdersDispatchFilters';
import DepartmentsFilter from './DepartmentsFilter';
import {
  DUMMY_QUERY_FILTERS,
  OrderDispatchQuery,
  useCollaboratorsQuery,
  useCustomersQuery,
  useDepartmentsQuery,
  useDispatchNumbersQuery,
  useDriversQuery,
  useDropOffSitesQuery,
  useDummyQuery,
  useEquipmentOwnersQuery,
  useEquipmentQuery,
  useEquipmentTypesQuery,
  useForemenQuery,
  useMaterialsQuery,
  usePickUpSitesQuery,
  useProjectExternalIdsQuery,
  useProjectsQuery,
  useRequestersQuery,
  useSalespeopleQuery,
  useServiceClassesQuery,
  useVendorsQuery,
} from './orders-dispatch-filters-queries/OrdersDispatchFiltersQueries';
import UnreadMessagesFilter from './UnreadMessagesFilter';

const OrdersDispatchFilters = observer(() => {
  const { allEntitiesLabel, categoryLabel, noEntitiesLabel } = useFilterCategoryLabels();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<SelectedFilter>();
  const [categorySearch, setCategorySearch] = useState('');
  const menuHandler = useRef<MenuHandler>(null);

  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get(DispatchURLParams.TAB) as StatusTabValue;

  const {
    availableOptions,
    updateAvailableOptions,
    updateAppliedFilterChipsCollectedData,
  } = useOrdersDispatchFilters();

  const quickDepartmentFiltersFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.quickDepartmentFilters,
  });

  const { userStore, ordersDispatchStore } = useStores();

  const aiReviewLoadsEnabled = userStore.userCompany.aiReviewLoads;
  const defaultDigitalTicketSettingEnabled = userStore.companySettings.isEnabled(
    'show_default_digital_ticket_view',
  );
  const companyId = getEffectiveUserCompanyId(userStore);
  const companyLegalName = userStore.currentCompanies?.[0]?.legalName ?? '';
  const noneOptionFilters = [SelectedFilter.DEPARTMENTS].concat(
    companyLegalName ? [SelectedFilter.VENDORS] : [],
  );

  const [searchQuery, setSearchQuery] = useState<{ input: string; debounced: string }>({
    input: '',
    debounced: '',
  });

  const debouncedSetSearchQuery = useRef(
    debounce((value: string) => {
      setSearchQuery((state) => ({ ...state, debounced: value }));
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
  ).current;

  const aiReviewStatusesQuery = useDummyQuery(availableOptions.aiReviewStatuses);
  const collaboratorsQuery = useCollaboratorsQuery(
    companyId,
    ordersDispatchStore.filters,
  );
  const customersQuery = useCustomersQuery(companyId, ordersDispatchStore.filters);
  const departmentsQuery = useDepartmentsQuery(companyId, ordersDispatchStore.filters);
  const dispatchNumbersQuery = useDispatchNumbersQuery(
    companyId,
    ordersDispatchStore.filters,
  );
  const driversQuery = useDriversQuery(companyId, ordersDispatchStore.filters);
  const dropOffSitesQuery = useDropOffSitesQuery(companyId, ordersDispatchStore.filters);
  const equipmentOwnersQuery = useEquipmentOwnersQuery(companyId, {
    id: companyId,
    name: companyLegalName,
  });
  const equipmentQuery = useEquipmentQuery(companyId, ordersDispatchStore.filters);
  const equipmentTypesQuery = useEquipmentTypesQuery(
    companyId,
    ordersDispatchStore.filters,
  );
  const foremenQuery = useForemenQuery(companyId, ordersDispatchStore.filters);
  const dispatchStatusesQuery = useDummyQuery(availableOptions.dispatchStatuses);
  const jobStatesQuery = useDummyQuery(availableOptions.jobStates);
  const materialsQuery = useMaterialsQuery(companyId, ordersDispatchStore.filters);
  const pickUpSitesQuery = usePickUpSitesQuery(companyId, ordersDispatchStore.filters);
  const projectExternalIdsQuery = useProjectExternalIdsQuery(companyId);
  const projectsQuery = useProjectsQuery(companyId, ordersDispatchStore.filters);
  const refinedOrderStatesQuery = useDummyQuery(availableOptions.refinedOrderStates);
  const requestersQuery = useRequestersQuery(companyId, ordersDispatchStore.filters);
  const salespeopleQuery = useSalespeopleQuery(companyId, ordersDispatchStore.filters);
  const serviceClassesQuery = useServiceClassesQuery(
    companyId,
    ordersDispatchStore.filters,
  );
  const siteTypesQuery = useDummyQuery(availableOptions.siteTypes);
  const prevailingWageQuery = useDummyQuery(availableOptions.prevailingWage);
  const ticketPotentialDuplicateQuery = useDummyQuery(availableOptions.duplicateTickets);
  const ticketTypesQuery = useDummyQuery(availableOptions.ticketTypes);
  const vendorsQuery = useVendorsQuery(companyId, ordersDispatchStore.filters);

  const [excludedFilters] = useMemo(() => {
    const excludedFilters: SelectedFilter[] = [];

    if (currentTab === StatusTabValue.PENDING) {
      excludedFilters.push(SelectedFilter.ORDER_STATES_REFINED);
    }

    if (!aiReviewLoadsEnabled) {
      excludedFilters.push(SelectedFilter.AI_REVIEW_STATUSES);
    }

    if (quickDepartmentFiltersFeatureFlagEnabled) {
      excludedFilters.push(SelectedFilter.DEPARTMENTS);
    }

    if (!defaultDigitalTicketSettingEnabled) {
      excludedFilters.push(SelectedFilter.TICKET_TYPES);
    }

    if (!ordersDispatchStore.isFetchingOrdersForASingleDay) {
      excludedFilters.push(SelectedFilter.DISPATCH_STATUSES);
    }

    return [excludedFilters];
  }, [
    aiReviewLoadsEnabled,
    currentTab,
    defaultDigitalTicketSettingEnabled,
    ordersDispatchStore.isFetchingOrdersForASingleDay,
    quickDepartmentFiltersFeatureFlagEnabled,
  ]);

  const applicableFilters = useMemo(() => {
    return Object.values(SelectedFilter).filter((filter) => {
      return !excludedFilters.includes(filter);
    });
  }, [excludedFilters]);

  const categoriesWithLabels = useMemo(
    () =>
      applicableFilters.map((filter) => ({
        filter,
        label: categoryLabel(filter),
      })),
    [applicableFilters, categoryLabel],
  );

  const activeFilters = useMemo(() => {
    const filtersWithSelections = new Set<SelectedFilter>();

    applicableFilters.forEach((filter) => {
      const key = ordersDispatchFilterKeyBySelectedFilter[filter];

      if ((ordersDispatchStore.filters[key] ?? []).length > 0) {
        filtersWithSelections.add(filter);
      }
    });

    return filtersWithSelections;
  }, [applicableFilters, ordersDispatchStore.filters]);

  const handleSelectCategory = useCallback((filter: SelectedFilter) => {
    setSelectedFilter(filter);
  }, []);

  const handleBackToCategories = useCallback(() => {
    setSelectedFilter(undefined);
    setCategorySearch('');
  }, []);

  const activeQuery = useMemo(() => {
    if (!selectedFilter) {
      return null;
    }

    const queries: Record<SelectedFilter, OrderDispatchQuery> = {
      [SelectedFilter.AI_REVIEW_STATUSES]: aiReviewStatusesQuery,
      [SelectedFilter.COLLABORATORS]: collaboratorsQuery,
      [SelectedFilter.CUSTOMERS]: customersQuery,
      [SelectedFilter.DEPARTMENTS]: departmentsQuery,
      [SelectedFilter.DISPATCH_NUMBERS]: dispatchNumbersQuery,
      [SelectedFilter.DISPATCH_STATUSES]: dispatchStatusesQuery,
      [SelectedFilter.DRIVERS]: driversQuery,
      [SelectedFilter.DROP_OFF_SITES]: dropOffSitesQuery,
      [SelectedFilter.EQUIPMENT]: equipmentQuery,
      [SelectedFilter.EQUIPMENT_OWNERS]: equipmentOwnersQuery,
      [SelectedFilter.EQUIPMENT_TYPES]: equipmentTypesQuery,
      [SelectedFilter.FOREMEN]: foremenQuery,
      [SelectedFilter.JOB_STATES]: jobStatesQuery,
      [SelectedFilter.MATERIALS]: materialsQuery,
      [SelectedFilter.ORDER_STATES_REFINED]: refinedOrderStatesQuery,
      [SelectedFilter.PICK_UP_SITES]: pickUpSitesQuery,
      [SelectedFilter.PREVAILING_WAGE]: prevailingWageQuery,
      [SelectedFilter.PROJECTS]: projectsQuery,
      [SelectedFilter.PROJECTS_EXTERNAL_IDS]: projectExternalIdsQuery,
      [SelectedFilter.REQUESTERS]: requestersQuery,
      [SelectedFilter.SALESPEOPLE]: salespeopleQuery,
      [SelectedFilter.SERVICE_CLASSES]: serviceClassesQuery,
      [SelectedFilter.SITE_TYPES]: siteTypesQuery,
      [SelectedFilter.DUPLICATE_TICKETS]: ticketPotentialDuplicateQuery,
      [SelectedFilter.TICKET_TYPES]: ticketTypesQuery,
      [SelectedFilter.VENDORS]: vendorsQuery,
    };

    return queries[selectedFilter];
  }, [
    selectedFilter,
    aiReviewStatusesQuery,
    collaboratorsQuery,
    customersQuery,
    departmentsQuery,
    dispatchNumbersQuery,
    dispatchStatusesQuery,
    driversQuery,
    dropOffSitesQuery,
    equipmentOwnersQuery,
    equipmentQuery,
    equipmentTypesQuery,
    foremenQuery,
    jobStatesQuery,
    materialsQuery,
    pickUpSitesQuery,
    prevailingWageQuery,
    projectExternalIdsQuery,
    projectsQuery,
    requestersQuery,
    salespeopleQuery,
    serviceClassesQuery,
    siteTypesQuery,
    ticketPotentialDuplicateQuery,
    ticketTypesQuery,
    vendorsQuery,
  ]);

  const isDummyQuery = useMemo(() => {
    return selectedFilter && DUMMY_QUERY_FILTERS.includes(selectedFilter);
  }, [selectedFilter]);

  const searchValueChangeCallback = useMemo(() => {
    if (isDummyQuery) {
      return undefined;
    }

    return (value: string) => setSearchQuery((state) => ({ ...state, input: value }));
  }, [isDummyQuery]);

  const fetchMoreCallback = useMemo(() => {
    return activeQuery?.fetchMore;
  }, [activeQuery?.fetchMore]);

  const selectedOptions = useMemo(() => {
    if (!selectedFilter) {
      return [];
    }

    const key = ordersDispatchFilterKeyBySelectedFilter[selectedFilter];
    const options = ordersDispatchStore.filters[key];

    return options ?? [];
  }, [ordersDispatchStore.filters, selectedFilter]);

  const handleSelectedOptionsChange = useCallback(
    (selectedOptions: string[]) => {
      if (selectedFilter) {
        const key = ordersDispatchFilterKeyBySelectedFilter[selectedFilter];
        const options = selectedOptions.length > 0 ? selectedOptions : undefined;

        ordersDispatchStore.setFilters({ [key]: options }, true);
      }
    },
    [selectedFilter],
  );

  const updateAvailableOptionsAndFilterChips = useCallback(
    (key: SelectedFilter, data: FilterMenuOption[]) => {
      updateAvailableOptions(key, data);
      updateAppliedFilterChipsCollectedData(key, data);
    },
    [],
  );

  const getNoneOptionLabel = (selectedFilter: SelectedFilter) => {
    if (noneOptionFilters.includes(selectedFilter)) {
      if (selectedFilter === SelectedFilter.DEPARTMENTS) {
        return t('dispatch.dispatch_v2.filters.departments_filter_none_option');
      }

      if (selectedFilter === SelectedFilter.VENDORS && companyLegalName) {
        return companyLegalName;
      }

      return noEntitiesLabel(selectedFilter);
    }
  };

  useEffect(() => {
    activeQuery?.fetch(searchQuery.debounced);
  }, [activeQuery?.fetch, searchQuery.debounced]);

  useEffect(() => {
    if (!isDummyQuery && selectedFilter && activeQuery?.data) {
      const noneOptionLabel = getNoneOptionLabel(selectedFilter);

      updateAvailableOptionsAndFilterChips(
        selectedFilter,
        activeQuery.data.concat(
          noneOptionLabel ? [{ label: noneOptionLabel, value: 'none' }] : [],
        ),
      );
    }
  }, [
    updateAvailableOptionsAndFilterChips,
    selectedFilter,
    activeQuery?.data,
    isDummyQuery,
  ]);

  useEffect(() => {
    debouncedSetSearchQuery(searchQuery.input);

    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery, searchQuery.input]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFilter(undefined);
      setSearchQuery((state) => ({ ...state, input: '' }));
      setCategorySearch('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!selectedFilter) {
      collaboratorsQuery.clear();
      customersQuery.clear();
      departmentsQuery.clear();
      dispatchNumbersQuery.clear();
      driversQuery.clear();
      dropOffSitesQuery.clear();
      equipmentOwnersQuery.clear();
      equipmentQuery.clear();
      equipmentTypesQuery.clear();
      foremenQuery.clear();
      materialsQuery.clear();
      pickUpSitesQuery.clear();
      projectExternalIdsQuery.clear();
      projectsQuery.clear();
      requestersQuery.clear();
      salespeopleQuery.clear();
      serviceClassesQuery.clear();
      vendorsQuery.clear();
    }
  }, [
    selectedFilter,
    collaboratorsQuery.clear,
    customersQuery.clear,
    departmentsQuery.clear,
    dispatchNumbersQuery.clear,
    driversQuery.clear,
    dropOffSitesQuery.clear,
    equipmentOwnersQuery.clear,
    equipmentQuery.clear,
    equipmentTypesQuery.clear,
    foremenQuery.clear,
    materialsQuery.clear,
    pickUpSitesQuery.clear,
    projectExternalIdsQuery.clear,
    projectsQuery.clear,
    requestersQuery.clear,
    salespeopleQuery.clear,
    serviceClassesQuery.clear,
    vendorsQuery.clear,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Menu
        ref={menuHandler}
        sx={{ [`& .${paperClasses.root}`]: { width: '250px' } }}
        onOpenStateChanged={setIsOpen}
        menuTrigger={
          <SmallButton
            size="small"
            startIcon={<FilterList />}
            sx={{
              [`&.${buttonClasses.root}`]: {
                bgcolor: theme.semantic.surfaceRaised,
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                borderRadius: theme.brandV2.borderRadius,
                color: theme.palette.text.primary,
              },
            }}
          >
            {t('common.filters')}
          </SmallButton>
        }
      >
        {selectedFilter && (
          <FilterMenuItem
            focusSearchFieldOnMount
            searchValue={searchQuery.input}
            onSearchValueChange={searchValueChangeCallback}
            onRequestBack={handleBackToCategories}
            loadingReason={activeQuery?.loadingReason}
            selectAllOptionLabel={
              (activeQuery?.data?.length ?? 0) <= 1
                ? undefined
                : allEntitiesLabel(selectedFilter)
            }
            options={activeQuery?.data ?? []}
            selectedOptions={selectedOptions}
            onSelectedOptionsChange={handleSelectedOptionsChange}
            onFetchMore={fetchMoreCallback}
            selectNoneOptionLabel={getNoneOptionLabel(selectedFilter)}
          />
        )}

        {!selectedFilter && (
          <FilterCategorySearch
            activeFilters={activeFilters}
            categories={categoriesWithLabels}
            focusSearchFieldOnMount
            onRequestClose={() => menuHandler.current?.onClose?.()}
            onSearchValueChange={setCategorySearch}
            onSelectCategory={handleSelectCategory}
            searchValue={categorySearch}
          />
        )}
      </Menu>
      <UnreadMessagesFilter />
      <DepartmentsFilter sx={{ ml: 1 }} />
    </Box>
  );
});

export default OrdersDispatchFilters;
